
import React from 'react';
import { Lead, LeadStatus } from '@/types/lead';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Star, ArrowLeft, RefreshCw } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

interface BusinessHeaderProps {
  lead: Lead;
  onToggleFavorite: () => void;
  onNavigateBack: () => void;
  onRefresh: () => void;
  isRefreshing?: boolean;
}

const BusinessHeader: React.FC<BusinessHeaderProps> = ({ 
  lead, 
  onToggleFavorite, 
  onNavigateBack,
  onRefresh,
  isRefreshing = false
}) => {
  const isMobile = useIsMobile();

  return (
    <div className="mb-4 space-y-3">
      
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-start sm:items-center gap-2 sm:gap-3 mb-2 flex-wrap">
            <h1 className="font-bold text-xl sm:text-2xl md:text-3xl break-words max-w-full">{lead.name}</h1>
            <div className="flex gap-1 sm:gap-2 flex-shrink-0">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={onToggleFavorite} 
                className={`h-8 w-8 sm:h-9 sm:w-9 ${lead.isFavorite ? "text-yellow-500" : "text-muted-foreground"}`}
                title={lead.isFavorite ? "Remove from favorites" : "Add to favorites"}
              >
                <Star className="h-5 w-5" fill={lead.isFavorite ? "currentColor" : "none"} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={onRefresh}
                disabled={isRefreshing}
                className="h-8 w-8 sm:h-9 sm:w-9"
                title="Refresh business data"
              >
                <RefreshCw className={`h-5 w-5 ${isRefreshing ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
          
          <p className="text-muted-foreground text-sm sm:text-base mb-3 break-words">{lead.address}</p>
          
          <div className="flex flex-wrap gap-2">
            {lead.category && (
              <Badge variant="outline" className="text-sm py-1">
                {lead.category}
              </Badge>
            )}
            {lead.rating && (
              <Badge variant="secondary" className="text-sm py-1">
                {lead.rating} ★ ({lead.reviewCount} reviews)
              </Badge>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessHeader;
